<template>
  <div
    class="recommended-select-item"
    :class="showSubtableIcons ? '' : 'noSubtableItems'"
  >
    <div class="toggle">
      <RadioButton
        v-if="props.recommendation.isExclusive"
        v-model="isSelected"
        :disabled="disabled"
        :input-id="props.parentId + '_' + props.recommendation.id"
        :value="props.recommendation.id"
      />
      <Checkbox
        v-else
        v-model="isSelected"
        :binary="true"
        :disabled="disabled"
      />
    </div>

    <div class="title-wrapper">
      <div class="label">
        <div class="title">
          <!-- TODO: Improve design -->
          {{ props.recommendation.name }}
          <span
            v-if="props.recommendation.detailDescription"
            class="info pi pi-info-circle"
            :class="props.expanded ? 'expanded' : ''"
            @click="() => emit(LayoutEvent.Toggle)"
          />
        </div>
        <div class="chip-wrapper">
          <div
            v-if="props.chip"
            class="chip brp-chip"
          >
            {{ props.chip }}
          </div>
          <div class="subtitle">
            {{ props.recommendation.description }}
          </div>
        </div>
      </div>
      <div
        v-if="props.showSubtableIcons"
        class="mobile-measure"
      >
        <div>
          <RecommendationRating
            :rating="props.recommendation.info.saving"
            icon-type="savings"
          />
        </div>
        <div>
          <RecommendationRating
            :rating="props.recommendation.info.comfort"
            icon-type="comfort"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRecommendations } from "@/store/recommendations";
import { Recommendation } from "@/types";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import Checkbox from "primevue/checkbox";
import RadioButton from "primevue/radiobutton";
import RecommendationRating from "@/components/result/recommendations/RecommendationRating.vue";
import { LayoutEvent } from "@/helpers";

const props = defineProps({
  parentId: {
    type: String,
    required: true,
  },
  recommendation: {
    type: Object as () => Recommendation,
    required: true,
  },
  showSubtableIcons: {
    type: Boolean,
    required: false,
  },
  expanded: {
    type: Boolean,
    required: false,
  },
  chip: {
    type: String,
    required: false,
  },
});

const emit = defineEmits<{ (event: LayoutEvent.Toggle): void }>();

const recommendationsStore = useRecommendations();
const { setRecommendationIsSelected } = recommendationsStore;
const { groupDictionary } = storeToRefs(recommendationsStore);

const isSelected = computed<boolean | string | undefined>({
  get() {
    return props.recommendation.isExclusive
      ? groupDictionary.value
          .get(props.parentId)
          ?.recommendations.find((x) => x.isSelected && x.isExclusive)?.id
      : props.recommendation.isSelected;
  },

  set(val: string | boolean | undefined) {
    if (props.recommendation.isExclusive) {
      setRecommendationIsSelected(
        props.parentId,
        props.recommendation.id,
        true
      );
    } else {
      setRecommendationIsSelected(
        props.parentId,
        props.recommendation.id,
        !!val
      );
    }
  },
});

const disabled = computed(() => {
  const group = groupDictionary.value.get(props.parentId);
  const upgraded = group?.recommendations
    .filter((r) => r.isExclusive)
    ?.find((r) => r.isUpgraded);
  return (
    (group ? !group.isSelected : true) ||
    (upgraded && props.recommendation.isExclusive ? true : false)
  );
});
</script>

<style lang="scss" scoped>
.recommended-select-item {
  display: flex;
  @media screen and (min-width: 720px) {
    &.noSubtableItems {
      width: max-content;
    }
  }
  .title-wrapper {
    display: flex;
    flex-direction: column;
  }
  :deep(.recommendation-rating svg) {
    width: 1rem;
    height: 1rem;
  }
  .toggle {
    display: flex;
    align-items: center;
    margin-right: 2rem;
    > * {
      margin: 0.5rem;
    }
    .icon {
      margin-left: 1rem;
    }
  }
  .label {
    display: flex;
    flex-direction: column;
    justify-content: center;
    .chip-wrapper {
      display: flex;
      gap: 0.5rem;
      align-items: center;
    }

    .chip {
      background-color: $secondary-color;
      padding: 0.15rem 0.75rem;
      border-radius: 0.5rem;
      color: $font-color-contrast;
      font-weight: 400;
      width: fit-content;
      height: fit-content;
      font-size: 0.9rem;
      margin: 0.25rem 0;
    }
  }

  .title {
    font-weight: bold;
    display: flex;
    align-items: center;
    gap: 0.25rem;

    .info {
      cursor: pointer;
    }
  }
}
</style>
